<script setup>
import { computed } from 'vue';
import { trans } from 'laravel-vue-i18n';
import { Head, Link, router } from '@inertiajs/vue3';
import StatusChip from '@/components/StatusChip';
import { useDashboardStore } from '@/stores/dashboard';
import { storeToRefs } from 'pinia';

const { stats, platform } = storeToRefs(useDashboardStore());

const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

const billingColors = {
    active: 'success',
    trialing: 'info',
    grace: 'warning',
    past_due: 'error',
    unpaid: 'error',
    incomplete: 'error',
    canceled: 'neutral',
    paused: 'neutral',
    none: 'neutral',
};

const platformSubtitle = computed(() => {
    return trans('label.platform_subtitle', {
        count: String(platform.value?.stats.dealerships_active ?? 0),
        mrr: platform.value?.stats.mrr ?? '',
    });
});

const tierSplitLine = computed(() => {
    const split = platform.value?.stats.tier_split ?? {};
    return Object.entries(split).map(([tier, count]) => `${count} ${tier}`).join(' · ');
});

const rowPercent = (row) => {
    if (!row.capacity_total) {
        return 0;
    }
    return Math.min(100, Math.round((row.capacity_stored / row.capacity_total) * 100));
};

const enterDealership = (row) => {
    router.post('/select-dealership', { dealership: row.hash });
};

const seasonNotice = computed(() => {
    const month = new Date().getMonth();
    const day = new Date().getDate();
    const isSummer = (month > 2 || (month === 2 && day >= 15)) && (month < 8 || (month === 8 && day < 15));
    return {
        label: isSummer ? trans('label.season_notice_summer') : trans('label.season_notice_winter'),
        classes: isSummer ? 'bg-summer-soft text-summer' : 'bg-winter-soft text-winter',
        icon: isSummer ? 'mdi-white-balance-sunny' : 'mdi-snowflake',
    };
});

const subtitle = computed(() => {
    return trans('label.dashboard_subtitle', {
        count: String(stats.value?.booked_today ?? 0),
        percent: String(capacityPercent.value),
    });
});

const seasonBreakdown = computed(() => {
    const breakdown = stats.value?.season_breakdown ?? {};
    return ['winter', 'summer', 'all_season']
        .filter((season) => breakdown[season])
        .map((season) => ({
            season,
            text: `${breakdown[season]} ${trans('label.' + season).toLowerCase()}`,
            color: { winter: 'text-winter', summer: 'text-summer', all_season: '' }[season],
        }));
});

const capacityPercent = computed(() => {
    const capacity = stats.value?.capacity;
    if (!capacity?.total) {
        return 0;
    }
    return Math.min(100, Math.round((capacity.stored / capacity.total) * 100));
});

const capacityPercentLabel = computed(() => `${capacityPercent.value}%`);

const capacityLabel = computed(() => {
    const capacity = stats.value?.capacity;
    return capacity ? `${capacity.stored} / ${capacity.total}` : '';
});

const storagePercent = (storage) => {
    if (!storage.capacity) {
        return 0;
    }
    return Math.min(100, Math.round((storage.stored / storage.capacity) * 100));
};

const storageLabel = (storage) => `${storage.stored} / ${storage.capacity}`;

const rackAdvice = computed(() => {
    const storages = stats.value?.storages ?? [];
    const full = storages.find((storage) => storagePercent(storage) >= 90);
    if (!full) {
        return null;
    }
    const target = storages
        .filter((storage) => storage !== full && storagePercent(storage) < 70)
        .sort((a, b) => storagePercent(a) - storagePercent(b))[0];
    if (!target) {
        return null;
    }
    return trans('label.nearly_full_notice', { full: full.name, target: target.name });
});

const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const activityMeta = (event) => [event.tire_barcode, formatDate(event.moved_at)].filter(Boolean).join(' · ');
</script>

<template>
<MainContentLayout>
    <Head :title="$t('label.dashboard')" />

    <template #title>{{ today }}</template>
    <template #breadcrumb>
        <p
            v-if="platform"
            class="text-sm text-ink-light">{{ platformSubtitle }}</p>
        <p
            v-else-if="stats"
            class="text-sm text-ink-light">{{ subtitle }}</p>
    </template>
    <template #actions>
        <span
            class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
            :class="seasonNotice.classes">
            <v-icon
                :icon="seasonNotice.icon"
                size="12" />
            {{ seasonNotice.label }}
        </span>
    </template>

    <template v-if="platform">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Link
                href="/dealerships"
                class="rounded-xl border border-line bg-surface p-5 hover:shadow-md transition no-underline text-ink">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.active_dealerships') }}</p>
                <p class="font-display font-bold text-3xl">{{ platform.stats.dealerships_active }}</p>
                <p class="text-xs mt-1 text-ink-light">{{ tierSplitLine }}</p>
            </Link>
            <div class="rounded-xl border border-line bg-surface p-5">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.monthly_revenue') }}</p>
                <p class="font-display font-bold text-3xl text-accent">{{ platform.stats.mrr }}</p>
                <p class="text-xs mt-1 text-ink-light">{{ $t('label.active_subscriptions', { count: platform.stats.subscriptions_active }) }}</p>
            </div>
            <div class="rounded-xl border border-line bg-surface p-5">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.billing_attention') }}</p>
                <p
                    class="font-display font-bold text-3xl"
                    :class="platform.stats.billing_attention > 0 ? 'text-warning' : 'text-success'">{{ platform.stats.billing_attention }}</p>
                <p class="text-xs mt-1 text-ink-light">{{ $t('label.billing_attention_hint') }}</p>
            </div>
            <div class="rounded-xl border border-line bg-surface p-5">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.storage_mode') }}</p>
                <p class="font-display font-bold text-3xl">{{ platform.stats.white_label }}</p>
                <p class="text-xs mt-1 text-ink-light">
                    {{ $t('label.mode_split', { white_label: platform.stats.white_label, shared: platform.stats.shared }) }}
                </p>
            </div>
        </div>

        <div class="grid lg:grid-cols-3 gap-6">
            <div class="rounded-xl border border-line bg-surface p-5 lg:col-span-2">
                <h3 class="font-display font-semibold text-base mb-4">{{ $t('label.dealership_health') }}</h3>
                <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="text-left text-xs font-semibold uppercase tracking-wider text-ink-light">
                                <th class="pb-2 pr-4">{{ $t('label.dealership') }}</th>
                                <th class="pb-2 pr-4">{{ $t('label.billing') }}</th>
                                <th class="pb-2 pr-4">{{ $t('label.sets_in_storage') }}</th>
                                <th class="pb-2 pr-4">{{ $t('label.rack_capacity') }}</th>
                                <th class="pb-2">{{ $t('label.last_activity') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in platform.dealerships"
                                :key="row.hash"
                                class="border-t border-line hover:bg-black/[0.02] cursor-pointer"
                                @click="enterDealership(row)">
                                <td class="py-2.5 pr-4">
                                    <span class="font-semibold">{{ row.name }}</span>
                                    <span class="block text-xs text-ink-light">{{ row.tier_name }}<template v-if="row.is_white_label">{{ ' · ' + $t('label.white_label') }}</template></span>
                                </td>
                                <td class="py-2.5 pr-4">
                                    <StatusChip :color="billingColors[row.billing_status] ?? 'neutral'">
                                        {{ $t('label.billing_status_' + row.billing_status) }}
                                    </StatusChip>
                                </td>
                                <td class="py-2.5 pr-4 font-mono">{{ row.sets_stored }}</td>
                                <td class="py-2.5 pr-4 min-w-[130px]">
                                    <div class="flex items-center gap-2">
                                        <div class="h-1.5 rounded-full overflow-hidden bg-canvas flex-1">
                                            <div
                                                class="h-full rounded-full"
                                                :class="rowPercent(row) >= 90 ? 'bg-warning' : 'bg-accent'"
                                                :style="{ width: rowPercent(row) + '%' }" />
                                        </div>
                                        <span class="text-xs text-ink-light font-mono">{{ rowPercent(row) + '%' }}</span>
                                    </div>
                                </td>
                                <td class="py-2.5 text-xs text-ink-light font-mono">{{ row.last_activity ?? '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="rounded-xl border border-line bg-surface p-5">
                <h3 class="font-display font-semibold text-base mb-4">{{ $t('label.recently_created') }}</h3>
                <ol
                    v-if="platform.recent.length"
                    class="space-y-3.5 text-sm">
                    <li
                        v-for="(entry, index) in platform.recent"
                        :key="index"
                        class="flex gap-3">
                        <span class="mt-1 w-2 h-2 rounded-full shrink-0 bg-accent" />
                        <div>
                            <p class="font-medium">{{ entry.name }}</p>
                            <p class="text-xs text-ink-light font-mono">{{ entry.created_at }}</p>
                        </div>
                    </li>
                </ol>
                <p
                    v-else
                    class="text-sm text-ink-light">{{ $t('label.no_records') }}</p>
            </div>
        </div>
    </template>

    <template v-else-if="stats">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Link
                href="/tire-sets"
                class="rounded-xl border border-line bg-surface p-5 hover:shadow-md transition no-underline text-ink">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.sets_in_storage') }}</p>
                <p class="font-display font-bold text-3xl">{{ stats.sets_in_storage }}</p>
                <p class="text-xs mt-1 text-ink-light">
                    <template v-for="(part, index) in seasonBreakdown" :key="part.season">
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                        <span v-if="index > 0"> · </span>
                        <span :class="part.color">{{ part.text }}</span>
                    </template>
                </p>
            </Link>
            <Link
                href="/requests"
                class="rounded-xl border border-line bg-surface p-5 hover:shadow-md transition no-underline text-ink">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.open_requests') }}</p>
                <p class="font-display font-bold text-3xl text-accent">{{ stats.open_requests }}</p>
            </Link>
            <Link
                href="/requests"
                class="rounded-xl border border-line bg-surface p-5 hover:shadow-md transition no-underline text-ink">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.completed_today') }}</p>
                <p class="font-display font-bold text-3xl text-success">{{ stats.completed_today }}</p>
            </Link>
            <Link
                href="/warehouse"
                class="rounded-xl border border-line bg-surface p-5 hover:shadow-md transition no-underline text-ink">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.rack_capacity') }}</p>
                <p
                    class="font-display font-bold text-3xl"
                    :class="capacityPercent >= 85 ? 'text-warning' : ''">{{ capacityPercentLabel }}</p>
                <p class="text-xs mt-1 text-ink-light font-mono">{{ capacityLabel }}</p>
            </Link>
        </div>

        <div class="grid lg:grid-cols-2 gap-6">
            <div class="rounded-xl border border-line bg-surface p-5">
                <h3 class="font-display font-semibold text-base mb-4">{{ $t('label.rack_utilization') }}</h3>
                <div
                    v-if="stats.storages.length"
                    class="space-y-3.5">
                    <div
                        v-for="storage in stats.storages"
                        :key="storage.name">
                        <div class="flex justify-between text-xs mb-1">
                            <span class="font-mono font-medium">{{ storage.name }}</span>
                            <span class="text-ink-light font-mono">{{ storageLabel(storage) }}</span>
                        </div>
                        <div class="h-2 rounded-full overflow-hidden bg-canvas">
                            <div
                                class="h-full rounded-full"
                                :class="storagePercent(storage) >= 90 ? 'bg-warning' : 'bg-accent'"
                                :style="{ width: storagePercent(storage) + '%' }" />
                        </div>
                    </div>
                </div>
                <p
                    v-else
                    class="text-sm text-ink-light">{{ $t('label.no_records') }}</p>
                <p
                    v-if="rackAdvice"
                    class="text-xs mt-4 rounded-lg px-3 py-2 bg-warning-soft text-warning">{{ rackAdvice }}</p>
            </div>

            <div class="rounded-xl border border-line bg-surface p-5">
                <h3 class="font-display font-semibold text-base mb-4">{{ $t('label.recent_activity') }}</h3>
                <ol
                    v-if="stats.recent_activity.length"
                    class="space-y-3.5 text-sm">
                    <li
                        v-for="(event, index) in stats.recent_activity"
                        :key="index"
                        class="flex gap-3">
                        <span
                            class="mt-1 w-2 h-2 rounded-full shrink-0"
                            :class="event.to_customer ? 'bg-accent' : 'bg-success'" />
                        <div>
                            <p class="font-medium">
                                {{ event.to_customer ? $t('label.returned_to_customer') : $t('label.racked') }}
                                <span
                                    v-if="event.location_label"
                                    class="font-mono text-xs">{{ event.location_label }}</span>
                            </p>
                            <p class="text-xs text-ink-light font-mono">{{ activityMeta(event) }}</p>
                        </div>
                    </li>
                </ol>
                <p
                    v-else
                    class="text-sm text-ink-light">{{ $t('label.no_records') }}</p>
            </div>
        </div>
    </template>
</MainContentLayout>
</template>
